import { DateTime } from 'luxon';

/**
 * Time helpers for the create-event form (CreateEventFormComponent), used by
 * every create surface — the new-user page and the in-app sheet alike.
 * Moved here from create-event-sheet.component.ts so both import one copy.
 */

/**
 * 15-minute slots as "h:mm AM/PM", matching the desktop create form's
 * generateTimeOptions() so every surface speaks the same time format.
 */
export function buildTimeOptions(): string[] {
  const times: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (const minute of [0, 15, 30, 45]) {
      const hour12 = hour % 12 === 0 ? 12 : hour % 12;
      const meridian = hour < 12 ? 'AM' : 'PM';
      times.push(`${hour12}:${minute.toString().padStart(2, '0')} ${meridian}`);
    }
  }
  return times;
}

/** "9:15 PM" → { hour: 21, minute: 15 }. */
export function parseSlot(slot: string): { hour: number; minute: number } | null {
  if (!slot) return null;
  const [time, meridian] = slot.trim().split(' ');
  if (!time || !meridian) return null;
  const [h, m] = time.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  let hour = h;
  if (meridian === 'PM' && h < 12) hour += 12;
  if (meridian === 'AM' && h === 12) hour = 0;
  return { hour, minute: m };
}

/** "9:15 PM" → "21:15", for ISO assembly. */
export function slotTo24h(slot: string): string | null {
  const parsed = parseSlot(slot);
  if (!parsed) return null;
  return `${parsed.hour.toString().padStart(2, '0')}:${parsed.minute.toString().padStart(2, '0')}`;
}

/** Floors to the 15-minute slot at or before `dt`, so "starts now" is never future-dated. */
export function floorToSlot(dt: DateTime): string {
  const minute = Math.floor(dt.minute / 15) * 15;
  const hour12 = dt.hour % 12 === 0 ? 12 : dt.hour % 12;
  const meridian = dt.hour < 12 ? 'AM' : 'PM';
  return `${hour12}:${minute.toString().padStart(2, '0')} ${meridian}`;
}

/** "2026-10-03" + "3:00 PM" → a DateTime in the local zone, or null. */
export function slotDateTime(date: string, slot: string): DateTime | null {
  const time24 = slotTo24h(slot || '');
  if (!date || !time24) return null;
  const dt = DateTime.fromISO(`${date}T${time24}`);
  return dt.isValid ? dt : null;
}

/** 4 → "4 hours", 3.5 → "3 hours 30 min", 24 → "1 day", 30 → "1 day 6 hours". */
export function durationLabel(hours: number): string {
  const totalMinutes = Math.round(hours * 60);
  const days = Math.floor(totalMinutes / 1440);
  const h = Math.floor((totalMinutes % 1440) / 60);
  const m = totalMinutes % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  if (h) parts.push(`${h} ${h === 1 ? 'hour' : 'hours'}`);
  if (m) parts.push(`${m} min`);
  return parts.join(' ') || '0 hours';
}

/** How an end reads next to its start: "7:00 PM" on the same day, otherwise "Wed 30 Sep, 12:15 AM". */
export function endLabel(start: DateTime, end: DateTime): string {
  return start.hasSame(end, 'day') ? end.toFormat('h:mm a') : end.toFormat('ccc d LLL, h:mm a');
}

/** End must follow start, and the API caps an event at 10 days. */
export function endAfterStartWithinTenDays(group: any) {
  const v = group.value;
  if (!v.startDate || !v.startTime || !v.endDate || !v.endTime) return null;

  const startT = slotTo24h(v.startTime);
  const endT = slotTo24h(v.endTime);
  if (!startT || !endT) return null;

  const start = new Date(`${v.startDate}T${startT}`).getTime();
  const end = new Date(`${v.endDate}T${endT}`).getTime();
  if (isNaN(start) || isNaN(end)) return null;

  if (end <= start) return { endBeforeStart: true };
  if ((end - start) / (1000 * 60 * 60 * 24) > 10) return { durationTooLong: true };
  return null;
}
